import { Fragment, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconExternalLink, IconInfoCircle } from '@tabler/icons-react';

import { Tabs, Tooltip, Typography } from '@zendesk/forethought-elements';
import ConversationTranscript from '../../ConversationTranscript';
import { TranscriptContainer } from '../../ConversationTranscript/RevampConversationTranscript';
import {
  ArticleTranscriptComponent,
  isArticleCardComponent,
  isRelatedArticlesTranscriptComponent,
} from '../../ConversationTranscript/types';
import { formatCSAT } from '../../helpers';
import { useAiQaTabs } from '../../hooks/useAiQaTabs';
import useGetCSATColor from '../../hooks/useGetCSATColor';
import useIsDeflectionInsightsEnabled from '../../hooks/useIsDeflectionInsightsEnabled';
import { useIsSentimentInsightsEnabled } from '../../hooks/useIsSentimentInsightsEnabled';
import { AiInstructionsSection } from '../AiInstructionsSection';
import AiQaChatDrawerTabContent from '../AiQaChatDrawerTabContent';
import { ArticlesSection } from '../ArticlesSection';
import { ContextVariables } from '../ContextVariables';
import { DeflectionInsightsData } from '../DeflectionInsightData';
import { ResolutionTierInsightsData } from '../ResolutionTierInsightsData';
import { SummaryCard } from '../SummaryCard';
import { formatTimeSpent } from '../utils';
import { VerdictCard } from '../VerdictCard';
import { AuditTranscript } from './AuditTranscript';
import { ErrorRollupCard } from './ErrorRollupCard';
import { IdentityGrid } from './IdentityGrid';
import { useConversationAudit } from './useConversationAudit';
import { useIsResolutionUiEnabled } from 'src/components/dashboard-pages/solve-insights/common/ResolutionInsightsCards';
import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import { useCanEditConversationReview } from 'src/hooks/useChannelAccess';
import { useIsHumanEvalsEnabled } from 'src/hooks/useHumanEvalsGates';
import { SentimentInsightCell } from 'src/pages/intent-conversation-analytics/components/SentimentInsightCell';
import { Conversation } from 'src/services/apiInterfaces';
import { ConversationReviewRating } from 'src/services/insights/types';

// The revamped transcript needs archive components, which only exist for
// conversations recorded after this date.
const REVAMPED_TRANSCRIPT_START_DATETIME = new Date('2025-01-09T00:00:00.000Z');

const MetaField = ({
  label,
  tooltip,
  value,
}: {
  label: string;
  tooltip?: string;
  value: ReactNode;
}) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
        <Typography color={palette.colors.grey[600]} variant='font14'>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip tooltipContent={tooltip}>
            <IconInfoCircle color={palette.colors.grey[500]} size={14} />
          </Tooltip>
        )}
      </Box>
      {typeof value === 'string' ? (
        <Typography noWrap variant='font14Medium'>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
};

export const AuditDrawerBody = ({
  onCreateNewInstruction,
  onOpenFeedbackPanel,
  selectedConversation,
}: {
  onCreateNewInstruction: (newId: string) => void;
  onOpenFeedbackPanel: (
    initialRating?: ConversationReviewRating,
    initialComment?: string | null,
  ) => void;
  selectedConversation: Conversation;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const getCSATColor = useGetCSATColor();
  const isDeflectionInsightsEnabled = useIsDeflectionInsightsEnabled();
  const isSentimentInsightsEnabled = useIsSentimentInsightsEnabled();
  const isResolutionUiEnabled = useIsResolutionUiEnabled();
  const isHumanEvalsEnabled = useIsHumanEvalsEnabled();
  const {
    feature_flags: dashboardFeatureFlags = [],
    human_evals_supported_channels: humanEvalsSupportedChannels = [],
  } = useOrgConfig();

  // nonce 0 is inert; each bump is one jump, aimed at `exchangeIndex` — or at
  // the specific error when `errorOrdinal` rides along.
  const [reveal, setReveal] = useState<{
    errorOrdinal?: number;
    exchangeIndex: number;
    nonce: number;
  }>({ exchangeIndex: -1, nonce: 0 });

  const {
    ai_qa_scores,
    channel,
    context_variables,
    conversation_id,
    conversation_status,
    csat_score,
    deflected,
    ending_sentiment_score,
    is_flagged,
    origin_information,
    pii_redacted,
    quick_feedback_responses,
    resolution_tier,
    resolution_tier_insights,
    start_datetime,
    summary,
    tagged_zendesk_ticket_source_id,
    tagged_zendesk_ticket_source_link,
    time_spent_in_widget,
    transcript,
    transcript_components,
    user_interactions,
    workflow_tag,
  } = selectedConversation;

  // While a conversation is still running its true deflection outcome isn't
  // known, so we show "Yes" until it finishes; once finished, the recorded
  // deflection status stands.
  const effectiveDeflected =
    conversation_status && conversation_status !== 'finished'
      ? true
      : deflected;

  const { audit } = useConversationAudit({
    conversationId: conversation_id,
    transcriptComponents: transcript_components,
  });

  const { selectedTab, setSelectedTab, showAiQaTab, tabs } =
    useAiQaTabs(ai_qa_scores);

  const isDisplaySummaryCardEnabled = dashboardFeatureFlags.includes(
    'display_summary_card',
  );
  // BE mirrors REVIEW_ENABLED_CHANNELS here — empty when human_evals is off.
  const canEditReview = useCanEditConversationReview(channel);
  const isReviewEnabledChannel =
    !!channel && humanEvalsSupportedChannels.includes(channel);

  const articleTranscriptComponents: ArticleTranscriptComponent[] =
    useMemo(() => {
      const result: ArticleTranscriptComponent[] = [];
      transcript_components?.forEach(component => {
        if (
          isArticleCardComponent(component) ||
          isRelatedArticlesTranscriptComponent(component)
        ) {
          result.push(component);
        }
      });
      return result;
    }, [transcript_components]);

  const contentSnippetTranscriptComponents = useMemo(
    () =>
      transcript_components?.filter(
        component => component.message_type === 'content_snippet',
      ) ?? [],
    [transcript_components],
  );

  const insightSections = [
    isResolutionUiEnabled && (
      <ResolutionTierInsightsData
        key='resolution'
        resolutionTier={resolution_tier}
        resolutionTierInsights={resolution_tier_insights}
      />
    ),
    quick_feedback_responses && quick_feedback_responses.length > 0 && (
      <ArticlesSection
        key='articles'
        onCreateNewInstruction={onCreateNewInstruction}
        quickFeedbackResponses={quick_feedback_responses}
        transcriptComponents={articleTranscriptComponents}
      />
    ),
    contentSnippetTranscriptComponents.length > 0 && (
      <AiInstructionsSection
        key='snippets'
        onCreateNewInstruction={onCreateNewInstruction}
        transcriptComponents={contentSnippetTranscriptComponents}
      />
    ),
    isDeflectionInsightsEnabled && (
      <DeflectionInsightsData
        key='deflection'
        selectedConversation={selectedConversation}
        showTopDivider={false}
      />
    ),
  ].filter((section): section is JSX.Element => Boolean(section));

  // The evidence is on the Conversation tab, so a jump switches there first and
  // lets the transcript open, scroll to and highlight the turn — landing on the
  // specific error row the rollup pointed at.
  const jumpToExchange = (exchangeIndex: number, errorOrdinal: number) => {
    setSelectedTab(0);
    setReveal(previous => ({
      errorOrdinal,
      exchangeIndex,
      nonce: previous.nonce + 1,
    }));
  };
  // Single-use: the transcript unmounts on the second tab, and a live nonce
  // would replay the jump on the way back.
  const consumeReveal = useCallback(
    () => setReveal({ exchangeIndex: -1, nonce: 0 }),
    [],
  );

  const canRenderAuditTranscript =
    audit &&
    transcript_components &&
    new Date(start_datetime) > REVAMPED_TRANSCRIPT_START_DATETIME;

  const isAwaitingIngestion =
    !start_datetime && !transcript_components?.length && !transcript?.length;

  if (isAwaitingIngestion) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          p: '40px',
          width: '100%',
        }}
      >
        <Typography variant='font24'>{t('drawerContent.title')}</Typography>
        <IdentityGrid
          channel={channel}
          conversationId={conversation_id}
          workflowTag={workflow_tag}
        />
        <Typography color={palette.colors.grey[600]} variant='font14'>
          {t('conversationAudit.awaitingIngestion')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        p: '40px',
        width: '100%',
      }}
    >
      <Typography variant='font24'>{t('drawerContent.title')}</Typography>

      <IdentityGrid
        channel={channel}
        conversationId={conversation_id}
        deflected={effectiveDeflected}
        resolutionTier={resolution_tier}
        startDatetime={start_datetime}
        workflowTag={workflow_tag}
      />

      {audit && <ErrorRollupCard audit={audit} onJump={jumpToExchange} />}

      {isDisplaySummaryCardEnabled && summary && (
        <SummaryCard conversationId={conversation_id} summary={summary} />
      )}

      {isHumanEvalsEnabled && isReviewEnabledChannel && (
        <VerdictCard
          conversationId={conversation_id}
          isFlagged={is_flagged}
          isReadOnly={!canEditReview}
          onOpenFeedbackPanel={onOpenFeedbackPanel}
        />
      )}

      {showAiQaTab && (
        <Tabs
          a11yProps={index => ({
            'aria-controls': `conversation-drawer-tabpanel-${index}`,
            id: `conversation-drawer-tab-${index}`,
          })}
          aria-label={t('revampDrawerBody.conversationDrawerTabsAriaLabel')}
          onChange={(_, value) => setSelectedTab(value)}
          tabs={tabs}
          typographyVariant='font14'
          value={selectedTab}
        />
      )}

      {selectedTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box
            sx={{
              border: `1px solid ${palette.colors.slate[200]}`,
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              p: '16px',
            }}
          >
            <Box
              sx={{
                columnGap: '24px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                rowGap: '16px',
              }}
            >
              {typeof time_spent_in_widget === 'number' && (
                <MetaField
                  label={t('revampDrawerBody.timeSpentLabel')}
                  value={formatTimeSpent(time_spent_in_widget)}
                />
              )}
              {typeof user_interactions === 'number' && (
                <MetaField
                  label={t('conversationAudit.userInteractionsLabel')}
                  value={String(user_interactions)}
                />
              )}
              {isSentimentInsightsEnabled && (
                <MetaField
                  label={t('filterOptions.sentiment')}
                  value={
                    <SentimentInsightCell
                      fontVariant='font14Medium'
                      sentimentScore={ending_sentiment_score}
                      timestamp={start_datetime}
                    />
                  }
                />
              )}
              <MetaField
                label={t('drawerBody.csatLabel')}
                value={
                  <Typography
                    color={getCSATColor(csat_score)}
                    variant='font14Medium'
                  >
                    {formatCSAT(csat_score)}
                  </Typography>
                }
              />
              {typeof pii_redacted === 'boolean' && (
                <MetaField
                  label={t('conversationAudit.redactionLabel')}
                  value={
                    pii_redacted
                      ? t('conversationAudit.redactionRedacted')
                      : t('conversationAudit.redactionNotRedacted')
                  }
                />
              )}
              {origin_information?.device_type && (
                <MetaField
                  label={t('conversationAudit.deviceTypeLabel')}
                  value={origin_information.device_type}
                />
              )}
              {origin_information?.os && (
                <MetaField
                  label={t('conversationAudit.osLabel')}
                  value={origin_information.os}
                />
              )}
              {tagged_zendesk_ticket_source_id &&
                tagged_zendesk_ticket_source_link && (
                  <MetaField
                    label={t('revampDrawerBody.liveConversationIdLabel')}
                    value={
                      <a
                        href={tagged_zendesk_ticket_source_link}
                        rel='noreferrer'
                        target='_blank'
                      >
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '2px',
                          }}
                        >
                          <Typography
                            color={palette.colors.blue[600]}
                            noWrap
                            variant='font14Medium'
                          >
                            {tagged_zendesk_ticket_source_id}
                          </Typography>
                          <IconExternalLink size={13} />
                        </Box>
                      </a>
                    }
                  />
                )}
            </Box>
          </Box>

          {context_variables && (
            <ContextVariables contextVariables={context_variables} />
          )}

          {insightSections.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${palette.colors.slate[200]}`,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                p: '16px',
              }}
            >
              {insightSections.map((section, index) => (
                <Fragment key={section.key}>
                  {index > 0 && (
                    <Box
                      component='hr'
                      sx={{
                        border: 0,
                        borderBottom: `1px solid ${palette.colors.slate[200]}`,
                        m: 0,
                        width: '100%',
                      }}
                    />
                  )}
                  {section}
                </Fragment>
              ))}
            </Box>
          )}

          {canRenderAuditTranscript ? (
            <AuditTranscript
              audit={audit}
              onRevealConsumed={consumeReveal}
              reveal={reveal}
              transcriptComponents={transcript_components}
            />
          ) : transcript?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Typography variant='font11'>
                {t('revampConversationTranscript.title')}
              </Typography>
              <TranscriptContainer>
                <ConversationTranscript
                  transcript={transcript}
                  variant='drawer'
                />
              </TranscriptContainer>
            </Box>
          ) : (
            <Typography color={palette.colors.grey[600]} variant='font14'>
              {t('conversationAudit.transcriptUnavailable')}
            </Typography>
          )}
        </Box>
      )}

      {selectedTab === 1 && showAiQaTab && ai_qa_scores && (
        <AiQaChatDrawerTabContent aiQaScores={ai_qa_scores} />
      )}
    </Box>
  );
};
